import React from 'react';
import { Minus, Plus } from 'lucide-react';
import './Form.css';
import Input from './Input';
import Textarea from './Textarea';
import Select from './Select';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/**
 * Baustein — Form-Layout.
 * Wiederverwendbare Hülle + Sub-Components für Formular-Patterns.
 *
 * Komposition:
 *   <Form>
 *     <SectionHeader divider>Titel</SectionHeader>
 *     <Form.Hint>Erklärtext.</Form.Hint>
 *
 *     <SectionHeader>Spieleranzahl</SectionHeader>
 *     <Form.Group>
 *       <Form.Option><input type="radio" name="p" /> Atraxa</Form.Option>
 *       <Form.Option><input type="radio" name="p" /> Edgar Markov</Form.Option>
 *     </Form.Group>
 *
 *     <Form.Stepper value={n} min={1} max={4} onChange={setN} ariaLabel="Teilnehmer" />
 *     <Form.Slider value={n} min={1} max={4} onChange={setN} ariaLabel="Teilnehmer" />
 *     <Form.PresetPills value={life} onChange={setLife} presets={[
 *       { value: 20, num: 20, label: 'Standard' },
 *       { value: 40, num: 40, label: 'Commander' },
 *     ]} />
 *
 *     <Form.Actions>
 *       <button className="btn-secondary">Abbrechen</button>
 *       <button className="btn-primary">Speichern</button>
 *     </Form.Actions>
 *   </Form>
 *
 * Props (Form):
 *   width — 'sm' | 'md' (Standard 'md') – matched die Modal-Größen
 */
export default function Form({ width = 'md', children, onSubmit }) {
  return (
    <form
      className={`form-card form-card--${width}`}
      onSubmit={onSubmit}
    >
      {children}
    </form>
  );
}

/** Vertikaler Stack von Inputs (Radios, Checkboxen, etc.) */
Form.Group = function FormGroup({ children }) {
  return <div className="form-group">{children}</div>;
};

/**
 * Option — ein Radio- oder Checkbox-Row mit Padding + Hover.
 * Ganze Zeile ist klickbar (Tap-Target auf Mobile).
 */
Form.Option = function FormOption({ children }) {
  return <label className="form-option">{children}</label>;
};

/** Hint — kurzer Kontext-Text unter Titel oder Section-Header. */
Form.Hint = function FormHint({ children }) {
  return <p className="form-hint">{children}</p>;
};

/** Actions — Footer-Zeile mit Buttons rechtsbündig. */
Form.Actions = function FormActions({ children }) {
  return <div className="form-actions">{children}</div>;
};

/**
 * Segmented — Pill-Reihe mit aktivem Segment.
 * Für kurze enum-Choices (2–5 Werte, kurze Labels).
 *
 * Props:
 *   value     — aktueller Wert (matched gegen options[i].value)
 *   onChange  — (value: string) => void
 *   options   — [{ value, label }]
 */
Form.Segmented = function FormSegmented({ value, onChange, options = [] }) {
  return (
    <div className="form-segmented" role="radiogroup">
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            className={`form-segmented__option${isActive ? ' form-segmented__option--active' : ''}`}
            aria-pressed={isActive}
            onClick={() => onChange?.(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Stepper — kompakter Number-Picker (− N +).
 * Inline-Block, passt in eine Label-Zeile als rechte Steuerung.
 *
 * Props:
 *   value      — aktueller Wert (number)
 *   min, max   — gültiger Bereich (inklusive)
 *   step       — Schrittweite, default 1
 *   onChange   — (value: number) => void
 *   ariaLabel  — wird als Präfix für „verringern"/„erhöhen" verwendet
 */
Form.Stepper = function FormStepper({ value, min = 0, max = 100, step = 1, onChange, ariaLabel = 'Wert' }) {
  return (
    <div className="form-stepper" role="group" aria-label={ariaLabel}>
      <button
        type="button"
        className="form-stepper__btn"
        onClick={() => onChange?.(clamp(value - step, min, max))}
        disabled={value <= min}
        aria-label={`${ariaLabel} verringern`}
      >
        <Minus size={14} />
      </button>
      <span className="form-stepper__num">{value}</span>
      <button
        type="button"
        className="form-stepper__btn"
        onClick={() => onChange?.(clamp(value + step, min, max))}
        disabled={value >= max}
        aria-label={`${ariaLabel} erhöhen`}
      >
        <Plus size={14} />
      </button>
    </div>
  );
};

/**
 * Slider — Range-Picker mit Custom-Styling (native input[type=range]).
 * Cross-Browser-konsistenter Track + Thumb. Volle Breite des Containers.
 *
 * Props:
 *   value      — aktueller Wert (number)
 *   min, max   — Bereich
 *   step       — Schrittweite, default 1
 *   onChange   — (value: number) => void
 *   ariaLabel  — für Screenreader
 */
Form.Slider = function FormSlider({ value, min, max, step = 1, onChange, ariaLabel }) {
  return (
    <input
      type="range"
      className="form-slider"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange?.(Number(e.target.value))}
      aria-label={ariaLabel}
    />
  );
};

/**
 * PresetPills — Pill-Reihe mit Quick-Picks für einen Wert.
 * Schmaler dezenter Stil — sitzt typischerweise direkt unter einem
 * Form.Slider und gehört visuell zu ihm.
 *
 * Props:
 *   value      — aktueller Wert
 *   onChange   — (value) => void
 *   presets    — [{ value, label, num? }] — num wird (falls vorhanden)
 *                vor dem label fett dargestellt
 */
Form.PresetPills = function FormPresetPills({ value, onChange, presets = [] }) {
  return (
    <div className="form-presets" role="group">
      {presets.map((p) => {
        const active = value === p.value;
        return (
          <button
            key={p.value}
            type="button"
            onClick={() => onChange?.(p.value)}
            className={`form-preset${active ? ' form-preset--active' : ''}`}
            aria-pressed={active}
          >
            {p.num != null && <span className="form-preset__num">{p.num}</span>}
            <span className="form-preset__label">{p.label}</span>
          </button>
        );
      })}
    </div>
  );
};

/**
 * Field — generischer Wrapper für Text/Select/Textarea/Number-Inputs.
 * Label oben, Input darunter, Hint/Error darunter.
 *
 * Children: genau ein `Input`, `Select` oder `Textarea` (auch als natives
 * <input>/<textarea> möglich). Ist `error` gesetzt und das Kind eines der
 * drei Feld-Bausteine, reicht `Field` seinen Fehlerzustand als `invalid`-Prop
 * an das Kind durch (Feld-Border rot statt nur Text unter dem Feld).
 *
 * Props:
 *   label     — Label-Text
 *   required  — boolean, zeigt * in Accent-Farbe
 *   hint      — optionaler Helper-Text (grau, klein)
 *   error     — optionaler Error-Text; ersetzt hint und färbt Input-Border rot
 */
Form.Field = function FormField({ label, required = false, hint, error, children }) {
  const classes = ['form-field'];
  if (error) classes.push('form-field--error');
  const child = React.isValidElement(children) ? children : null;
  const isPrimitive = child && (child.type === Input || child.type === Textarea || child.type === Select);
  const control = error && isPrimitive
    ? React.cloneElement(child, { invalid: true })
    : children;
  return (
    <label className={classes.join(' ')}>
      <span className="form-field__label">
        {label}
        {required && <span className="form-field__required" aria-hidden> *</span>}
      </span>
      {control}
      {error
        ? <span className="form-field__error" role="alert">{error}</span>
        : hint && <span className="form-field__hint">{hint}</span>}
    </label>
  );
};
